import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import FundingCalculator from "@/components/site/FundingCalculator";

export default function Hero({ onApply }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative hero-gradient text-white pt-32 pb-40 overflow-hidden grain"
    >
      {/* subtle stars */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        {Array.from({ length: 40 }).map((_, i) => (
          <span
            key={i}
            className="absolute bg-white rounded-full"
            style={{
              width: Math.random() * 2 + 1 + "px",
              height: Math.random() * 2 + 1 + "px",
              top: Math.random() * 60 + "%",
              left: Math.random() * 100 + "%",
              opacity: Math.random() * 0.7 + 0.2,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
        {/* Left */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-purple-200"
          >
            <Sparkles className="w-3.5 h-3.5" /> Funding in as fast as 24 hours
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="font-display text-5xl md:text-6xl lg:text-7xl font-black leading-[1.02] mt-6"
          >
            Fast funding <br />
            for{" "}
            <span className="bg-gradient-to-r from-purple-300 via-fuchsia-300 to-sky-300 bg-clip-text text-transparent">
              real growth
            </span>
            .
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.7 }}
            className="mt-6 text-lg md:text-xl text-white/80 max-w-xl leading-relaxed"
          >
            Rocket Cap Funding matches capital to your real-world income — from
            covering short-term cash gaps to fueling your next stage of growth.
            Simple, flexible, and built to move at the speed of your business.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-apply-btn"
              onClick={() => onApply({})}
              className="group inline-flex items-center gap-2 rounded-full bg-white text-[#0b1121] font-bold px-8 py-4 text-sm tracking-[0.15em] uppercase shadow-2xl hover:shadow-purple-500/50 hover:-translate-y-0.5 transition-all"
            >
              Apply for Funding
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
            </button>
            <a
              href="#how-we-work"
              data-testid="hero-how-link"
              className="text-sm font-semibold uppercase tracking-[0.15em] text-white/70 hover:text-white border-b-2 border-white/20 hover:border-white pb-1 transition"
            >
              How it works
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-lg"
          >
            {[
              { k: "$500M+", v: "Funded to date" },
              { k: "24 hrs", v: "Fastest funding" },
              { k: "10,000+", v: "Businesses funded" },
            ].map((s) => (
              <div key={s.k}>
                <div className="font-display text-2xl md:text-3xl font-extrabold text-white">
                  {s.k}
                </div>
                <div className="text-xs text-white/60 mt-1 uppercase tracking-wider">
                  {s.v}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right — Calculator */}
        <motion.div
          initial={{ opacity: 0, x: 40, scale: 0.97 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
        >
          <FundingCalculator onApply={onApply} />
        </motion.div>
      </div>

      {/* clouds bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-32 clouds-bottom pointer-events-none" />
    </section>
  );
}
